// Service page content from PDF
export const services = {
  'fractional-cmo': {
    slug: 'fractional-cmo',
    title: 'Fractional CMO',
    tagline: 'For B2B teams that have outgrown tactical marketing and need commercial leadership without the full-time hire.',
    hero: {
      headline: 'Your marketing needs a leader,',
      headlineAccent: 'not just more activity.',
      subheadline: 'We bring senior marketing leadership into your business, so you can stop carrying the strategic weight yourself, and start building growth that actually compounds.',
    },
    problem: {
      label: 'The Problem',
      headline: "Activity without leadership isn't a marketing function. It's organised chaos.",
      intro: 'The team is working. The channels are running. The reports look busy. But there\'s no coherent system behind any of it, which means growth stays inconsistent, pipeline stays unpredictable, and leadership keeps filling the strategic gap themselves. That\'s not sustainable. And it\'s not what your business needs.',
      bullets: [
        'Marketing is happening without a clear strategic agenda behind it',
        'Junior teams are being asked to make senior decisions',
        'Founders and leadership are carrying marketing direction on top of everything else',
        'Reporting tracks motion but not commercial momentum',
      ],
    },
    truth: {
      label: 'What a Fractional CMO Actually Does',
      headline: "It's the role that connects strategy, execution, and commercial accountability.",
      intro: 'A Fractional CMO gives you access to senior marketing leadership without the overhead of a full-time hire. The result is better marketing, aimed at outcomes that matter to the business.',
      bullets: [
        'Marketing roadmap ownership so there\'s always a clear direction',
        'Positioning and messaging oversight so everything speaks the same language',
        'KPI frameworks tied to pipeline, not only traffic and impressions',
        'Team and vendor direction so execution stays aligned with strategy',
        'Quarterly growth planning so the business moves forward with intent',
      ],
    },
    whatYouGet: {
      label: 'What You Get',
      headline: 'Clarity first. Momentum after.',
      intro: 'This engagement is designed to diagnose before it prescribes. We find out what\'s actually holding the marketing back before recommending what to build.',
      bullets: [
        'A clear diagnosis of the gaps holding growth back',
        'A prioritised roadmap, what to do first and why',
        'Sharper positioning and message direction',
        'Better decisions around channels, campaigns, and investment',
        'Reporting that ties activity to business outcomes, not just activity',
      ],
    },
    process: [
      { num: '01', title: 'Audit', body: 'We review your current marketing, positioning, and pipeline picture, honestly and without the jargon jumble.' },
      { num: '02', title: 'Diagnose', body: 'We identify where the system is breaking, where the real leverage is, and what should happen next.' },
      { num: '03', title: 'Roadmap', body: 'We define a sequence of work built around commercial impact, not noise or best guesses.' },
      { num: '04', title: 'Lead', body: 'We stay close to the business, guiding execution and refining priorities as the market shifts.' },
    ],
    forYou: [
      'You need senior B2B marketing thinking, not more activity for activity\'s sake',
      'Your team can execute, but there\'s no clear strategic lead making the calls',
      'You want marketing to contribute to pipeline in a way you can actually measure',
      'You\'re ready to build a marketing system that compounds over time without resetting every quarter',
    ],
    notForYou: [
      'You want a generalist agency running disconnected tactics',
      'You\'re looking for fast fixes without strategic foundations',
      'You\'re not interested in tying marketing to commercial outcomes',
      'You prefer volume over clarity',
    ],
    notForYouFooter: 'And that\'s okay. We\'d rather be honest upfront than wrong together later.',
    why: {
      headline: "We're built for B2B and nothing else.",
      intro: 'Long sales cycles, multi-stakeholder buying, and the need to prove that marketing is doing more than generating activity, that\'s the environment we\'ve designed every service around. Strategy leads here. Execution follows. And pipeline is the only scorecard that matters.',
      bullets: [
        'Deep B2B specialisation',
        'Strategy before execution, always',
        'Pipeline as the real measure',
        'Systems that compound, not campaigns that expire',
        'Education-first thinking at every stage',
      ],
    },
    faqs: [
      { q: 'What is a Fractional CMO?', a: 'A senior marketing leader who works with your business on a part-time or engagement basis to provide strategic direction, leadership, and commercial accountability, without the full-time cost.' },
      { q: 'How is this different from an agency?', a: 'An agency focuses on delivery. A Fractional CMO focuses on strategy, prioritisation, and making sure the delivery is actually pointed at the right outcomes.' },
      { q: 'How is this different from hiring a full-time CMO?', a: 'You get senior leadership at the level your business actually needs right now, without the overhead of a full-time salary, benefits, and long-term commitment.' },
      { q: 'What kinds of businesses is this best for?', a: 'Growth-stage B2B SaaS companies, B2B service businesses, and mid-market teams that need senior marketing structure but aren\'t ready for a full-time hire.' },
      { q: 'Do you only advise, or do you help execute too?', a: 'Both, depending on the engagement. The core role is strategic leadership, but we can stay involved in execution, reporting, and optimisation as needed.' },
      { q: 'What happens after I book a strategy call?', a: 'You book a discovery call. We review your current setup together, ask the right questions, and give you an honest assessment of whether a Fractional CMO engagement is the right next step.' },
    ],
    cta: {
      headline: 'Ready for senior marketing leadership that actually moves the business forward?',
      body: 'If your marketing needs structure, clarity, and accountability — the next step is simple. Book a Free Strategy Call and we\'ll review your current marketing setup together.',
    },
  },
  'positioning-messaging': {
    slug: 'positioning-messaging',
    title: 'Positioning & Messaging',
    tagline: 'For businesses that know they\'re strong, but aren\'t yet communicating it clearly enough.',
    hero: {
      headline: 'Your business is stronger',
      headlineAccent: 'than the way you\'re currently describing it.',
      subheadline: 'We help ambitious B2B businesses turn vague, broad messaging into sharp positioning, the kind that makes buyers understand quickly, trust easily, and choose confidently.',
    },
    problem: {
      label: 'The Problem',
      headline: "Most B2B businesses don't have a visibility problem first. They have a clarity problem.",
      intro: 'The offer is strong, but the message is too broad. The website sounds polished, but not specific. The team understands the product deeply, but the market can\'t quickly tell why it matters or why to choose you over anyone else.',
      bullets: [
        'Positioning is generic, so the business blends in with every competitor',
        'Messaging is full of features but weak on commercial meaning',
        'The value is real, but it\'s not framed in a way buyers can repeat or remember',
        'The offer is good, but too many people still need it explained on every call',
      ],
    },
    truth: {
      label: 'What Positioning Really Does',
      headline: 'Positioning is the foundation that makes every other marketing decision easier.',
      intro: 'It shapes how you describe the business, which buyers you attract, what problems you own in the market, and how confidently you can price and sell. Without it, campaigns become noisier, content becomes broader, and sales conversations do too much heavy lifting.',
      bullets: [
        'Clarifies who you\'re actually for',
        'Sharpens what problem you solve and how',
        'Defines why you\'re meaningfully different',
        'Improves how quickly buyers understand your value',
        'Gives your marketing a commercial anchor to work from',
      ],
    },
    whatYouGet: {
      label: 'What You Get',
      headline: 'Strategic clarity you can use everywhere. More than a better copy.',
      intro: 'We turn unclear positioning into a structured message system you can use across your website, LinkedIn, sales conversations, and future campaigns.',
      bullets: [
        'A sharper positioning point of view that holds up in any room',
        'A clearer explanation of what you do and why it matters, that buyers can repeat',
        'Stronger website and sales messaging',
        'A more compelling offer narrative',
        'A foundation for all future content, campaigns, and marketing',
      ],
    },
    process: [
      { num: '01', title: 'Audit', body: 'We review how you currently talk about the business and identify exactly where clarity breaks down.' },
      { num: '02', title: 'Research', body: 'We look at your buyers, competitors, market language, and current perception from the outside.' },
      { num: '03', title: 'Position', body: 'We define the strategic angle that makes the business easier to understand and harder to ignore.' },
      { num: '04', title: 'Message', body: 'We turn that strategy into usable messaging — for your website, social, sales conversations, and future campaigns.' },
    ],
    forYou: [
      'Your business is strong, but the message doesn\'t reflect that yet',
      'Your website feels broad, vague, or too \'agency-like\'',
      'Buyers need too much explanation before they understand the offer',
      'You want positioning that supports growth, stronger pricing, and faster trust',
    ],
    notForYou: [
      'You want cosmetic copy changes without any strategic work behind them',
      'You think the market already fully understands your offer',
      'You want generic branding language instead of commercial clarity',
      'You\'re not willing to make the business easier to say and easier to buy',
    ],
    notForYouFooter: 'We\'d rather be honest about fit upfront than deliver work that doesn\'t move anything.',
    why: {
      headline: "We don't treat positioning as a creative exercise. We treat it as a commercial one.",
      intro: 'Our work is built for B2B buying cycles where clarity, credibility, and repeatability matter more than clever phrasing. We help businesses sound like the value they actually deliver, not a diluted version of it.',
      bullets: [
        'Clarity first, always',
        'Strategy before words',
        'Commercial language over filler',
        'Deep B2B specialisation',
        'Messaging built to support pipeline, not just awareness',
      ],
    },
    faqs: [
      { q: 'What is B2B positioning?', a: 'It\'s how your business defines its place in the market — who it serves, what it solves, and why it\'s meaningfully different from every other option in the room.' },
      { q: 'Why does positioning matter so much?', a: 'Because if buyers can\'t quickly understand what you do and why it matters, they\'re far less likely to trust you, remember you, or choose you — regardless of how good the offer actually is.' },
      { q: 'Is this just copywriting?', a: 'No. Copywriting is part of the output, but positioning starts with strategy, market understanding, and offer clarity. The words come after thinking.' },
      { q: 'Do you work with startups as well as established businesses?', a: 'Yes. We work with early-stage B2B startups, growth-stage SaaS businesses, service firms, and senior marketers who need sharper market clarity.' },
      { q: 'What happens after I book a strategy call?', a: 'You book a discovery call. We review your current positioning, messaging, and market perception together, and identify where the real gaps are.' },
    ],
    cta: {
      headline: 'Ready to make your business easier to understand and easier to buy from?',
      body: 'If your positioning is holding the business back, start with an audit. It\'s free and it\'s honest. Book a FREE Strategy Call and we\'ll review your current positioning and messaging together.',
    },
  },
  'demand-generation': {
    slug: 'demand-generation',
    title: 'Demand Generation',
    tagline: 'For B2B businesses that need more than leads — they need pipeline that compounds.',
    hero: {
      headline: 'Your pipeline shouldn\'t depend on',
      headlineAccent: 'who you happen to know this month.',
      subheadline: 'We build structured demand generation systems that bring the right buyers into your world, move them forward with purpose, and convert consistent attention into qualified commercial opportunities.',
    },
    problem: {
      label: 'The Problem',
      headline: "Most demand generation fails for the same reason: it's built around activity, not intent.",
      intro: 'Teams launch campaigns, publish content, and chase leads, but the system underneath isn\'t designed to attract the right buyers or move them forward with enough clarity. The result is inconsistent lead quality, wasted effort, and pipeline that never quite compounds the way it should.',
      bullets: [
        'Lead volume is being chased without enough qualification behind it',
        'Campaigns are being launched before the messaging is clear enough to convert',
        'Content exists, but it\'s not built to move buyers closer to a decision',
        'The business is generating activity, not sales momentum',
      ],
    },
    truth: {
      label: 'What Demand Generation Really Is',
      headline: 'The process of creating awareness, trust, and buying intent.',
      intro: 'Done well, demand generation brings the right people into your world, educates them properly, and moves them toward a commercial conversation with less friction. That means your marketing has to be built around how your buyer actually thinks and decides.',
      bullets: [
        'Attract the RIGHT audience',
        'Educate with purpose at every stage of the journey',
        'Build trust before you ask for the conversion',
        'Convert interest into qualified pipeline',
        'Give sales better inputs so they can close faster',
      ],
    },
    whatYouGet: {
      label: 'What You Get',
      headline: 'Better opportunities with more consistency, not just more names in the CRM.',
      intro: 'We design demand generation systems built for B2B buying cycles — combining strategy, content, outreach, and campaign logic in a way that supports how your buyers actually evaluate a solution.',
      bullets: [
        'A clearer demand generation strategy tied to your specific ICP and sales cycle',
        'Better quality leads from the right channels',
        'Messaging that supports conversion, not just clicks',
        'A pipeline process built to compound every quarter',
        'Campaigns tied to commercial outcomes you can actually measure',
      ],
    },
    process: [
      { num: '01', title: 'Audit', body: 'We review your current demand engine, active channels, and conversion points honestly.' },
      { num: '02', title: 'Strategy', body: 'We define where demand should come from, who it should attract, and how it should move through the funnel.' },
      { num: '03', title: 'Build', body: 'We develop the campaigns, offers, and systems that support the strategy, not the other way around.' },
      { num: '04', title: 'Optimise', body: 'We measure what\'s creating a qualified pipeline and refine everything else from there.' },
    ],
    forYou: [
      'You need better pipeline quality, not just more leads in a spreadsheet',
      'Your current campaigns feel disconnected from what actually closes',
      'You want a system that compounds instead of resetting every month',
      'You\'re ready to build demand around strategic clarity, not channel availability',
    ],
    notForYou: [
      'You want random lead volume with no qualification attached',
      'You expect one campaign to solve a structural problem',
      'You\'re not willing to align marketing activity with sales outcomes',
      'You want execution without a strategy to anchor it',
    ],
    notForYouFooter: 'Honest mismatch now saves everyone time later.',
    why: {
      headline: "We don't treat demand generation as a channel exercise. We treat it as a commercial system.",
      intro: 'Our work is designed for B2B buying cycles — where trust, timing, and clarity matter more than campaign volume. We focus on the systems that create pipeline you can actually measure and build on.',
      bullets: [
        'Built for B2B from the ground up',
        'Strategy-led from the very first conversation',
        'Pipeline-focused, not vanity metric-focused',
        'Systems over sprints',
        'Education-first thinking at every stage of the funnel',
      ],
    },
    faqs: [
      { q: 'What is B2B demand generation?', a: 'It\'s the process of creating awareness, trust, and buying intent so the right prospects move toward a commercial conversation rather than waiting for referrals or hoping the timing is right.' },
      { q: 'How is this different from lead generation?', a: 'Lead generation captures interest. Demand generation creates the conditions that make interest more likely in the first place. One is a tactic. The other is a system.' },
      { q: 'What channels do you use?', a: 'That depends on the business and where your buyers actually spend time. Common channels include content, email, LinkedIn, outbound sequences, lead magnets, and paid campaigns.' },
      { q: 'Do you work with companies that already have a marketing team?', a: 'Yes. We often support teams that have execution capability but need stronger strategic structure, better messaging, or a smarter campaign framework behind what they\'re already doing.' },
      { q: 'What happens after I book a strategy call?', a: 'You book a discovery call. We review your current demand generation setup together and identify exactly where the pipeline is breaking down.' },
    ],
    cta: {
      headline: 'Ready to build demand that creates a real pipeline?',
      body: 'If your current marketing isn\'t producing the quality or consistency you need, start with an audit. It\'s free and it\'s honest. Book a FREE Strategy Call and we\'ll review your current demand generation setup together.',
    },
  },
  'content-seo': {
    slug: 'content-seo',
    title: 'Content & SEO Strategy',
    tagline: 'For businesses that need content with commercial purpose.',
    hero: {
      headline: 'Content that makes your business',
      headlineAccent: 'easier to find and impossible to dismiss.',
      subheadline: 'We help ambitious B2B businesses build content systems that create real search visibility, establish genuine credibility, and turn consistent attention into pipeline over time.',
    },
    problem: {
      label: 'The Problem',
      headline: "Most B2B content fails because it's created for activity, not intent.",
      intro: 'There are posts, blogs, and updates, but they\'re not built around the questions buyers are actually asking. The result is content that looks active, maybe even impressive in a deck, but doesn\'t help the business win attention, build trust, or support anything close to a commercial outcome.',
      bullets: [
        'Content is being published without a clear strategy behind it',
        'SEO is treated as traffic work, not commercial work',
        'The website has information on it, but not a strong resource architecture',
        'Thought leadership exists, but it doesn\'t move buyers any closer to a decision',
      ],
    },
    truth: {
      label: 'What Content & SEO Should Actually Do',
      headline: 'Good content makes the business easier to understand before a sales conversation ever happens.',
      intro: 'It answers real questions, supports your positioning, and builds trust across the full buyer journey. SEO should do the same — bringing the right visitors with the right intent at the right moment.',
      bullets: [
        'Capture high-intent search demand from buyers actively looking',
        'Educate buyers at every stage so sales conversations start further along',
        'Support positioning and category clarity over time',
        'Build trust before the first call is ever booked',
        'Create content assets that compound in value every month',
      ],
    },
    whatYouGet: {
      label: 'What You Get',
      headline: "A content system that's strategic, usable, and built to compound.",
      intro: 'We build content and SEO systems designed for B2B decision-makers. We don\'t start with volume. We start with intent — what your buyers are searching for, what questions they\'re asking, and what topics matter commercially.',
      bullets: [
        'A clearer content direction tied to commercial priorities',
        'Keyword and topic mapping built around actual buyer intent',
        'Content pillars that support the business',
        'Stronger website and resource architecture',
        'A content system that earns trust and authority over time',
      ],
    },
    process: [
      { num: '01', title: 'Audit', body: 'We review your current content, website structure, and search visibility and tell you what\'s actually working.' },
      { num: '02', title: 'Intent Mapping', body: 'We identify what your buyers are searching for, asking, and comparing before they ever reach out.' },
      { num: '03', title: 'Strategy', body: 'We define the topics, pages, and content formats that matter most to the business commercially.' },
      { num: '04', title: 'System', body: 'We build a content and SEO structure designed to support visibility and long-term compounding growth.' },
    ],
    forYou: [
      'You want content that supports growth, not only engagement or vanity metrics',
      'Your website needs stronger structure and a clearer topical focus',
      'You want to attract buyers through search and genuine authority',
      'You need SEO and content to work together more intelligently',
    ],
    notForYou: [
      'You want low-value blog output with no strategic direction attached',
      'You\'re chasing traffic without thinking seriously about what happens after the click',
      'You want content for the sake of consistency alone without commercial intent',
      'You\'re not willing to build around what your buyers actually care about',
    ],
    notForYouFooter: 'We\'d rather point you somewhere better than take on work that won\'t move the needle.',
    why: {
      headline: 'We treat content and SEO as part of the commercial system.',
      intro: 'Our approach is built for B2B buyers who search differently, evaluate more carefully, and need more proof before they commit. We create content that\'s genuinely useful, structurally sound, and aligned with what the business is actually trying to achieve.',
      bullets: [
        'B2B-first strategy, always',
        'Search intent over keyword guesswork',
        'Content built for buyers, not bots',
        'Clarity before volume',
        'Education that actively supports pipeline',
      ],
    },
    faqs: [
      { q: 'What is B2B content strategy?', a: 'It\'s the plan for what content you create, why you create it, who it\'s for, and how it supports business growth — so everything you publish has a reason to exist beyond filling a calendar.' },
      { q: 'How is SEO different in B2B?', a: 'B2B SEO is more intent-driven. It tends to focus on longer buying cycles, more specific research questions, and commercially valuable search terms.' },
      { q: 'Do you only do blog content?', a: 'No. We shape the full content system including website pages, pillar content, SEO architecture, and founder or LinkedIn content where relevant.' },
      { q: 'How do you choose topics?', a: 'Based on buyer intent, search demand, positioning priorities, and what will actually support the business commercially. Not based on what\'s trending or what\'s easiest to write.' },
      { q: 'What happens after I book a strategy call?', a: 'You book a discovery call. We review your current content, SEO setup, and visibility gaps together and give you an honest read on what\'s worth doing first.' },
    ],
    cta: {
      headline: 'Ready to make your content more strategic and more commercially useful?',
      body: 'If your current content isn\'t helping the business grow, start with an audit. It\'s free and you\'ll leave knowing exactly what to fix. Book a FREE Strategy Call and we\'ll review your current content and SEO setup together.',
    },
  },
};

export const serviceList = [
  { slug: 'fractional-cmo', title: 'Fractional CMO', tagline: 'Senior marketing leadership without the full-time hire.', icon: 'Crown' },
  { slug: 'positioning-messaging', title: 'Positioning & Messaging', tagline: 'Turn vague messaging into sharp positioning buyers can repeat.', icon: 'Target' },
  { slug: 'demand-generation', title: 'Demand Generation', tagline: 'Structured systems that turn attention into qualified pipeline.', icon: 'TrendingUp' },
  { slug: 'content-seo', title: 'Content & SEO Strategy', tagline: 'Content systems that build search visibility and credibility.', icon: 'FileText' },
];
